import { useCallback, useState, type DragEvent } from 'react';
import type { FileType } from '@/routes/chat/hooks/use-chat';

export interface UseFileDragDropOptions {
  onFilesDropped?: (files: File[]) => void;
  onFileMove?: (sourceFile: FileType, targetPath: string) => void;
  onFolderMove?: (sourcePath: string, targetPath: string) => void;
  accept?: string[];
  disabled?: boolean;
  allowFileUpload?: boolean;
  allowFileReorganization?: boolean;
}

export interface UseFileDragDropReturn {
  isDragging: boolean;
  dragOverTarget?: string;
  dragHandlers: {
    onDragEnter: (e: DragEvent) => void;
    onDragOver: (e: DragEvent) => void;
    onDragLeave: (e: DragEvent) => void;
    onDrop: (e: DragEvent) => void;
  };
  fileDragHandlers: {
    onDragStart: (e: DragEvent, file: FileType) => void;
    onDragEnd: (e: DragEvent) => void;
  };
  folderDropHandlers: (folderPath: string) => {
    onDragEnter: (e: DragEvent) => void;
    onDragOver: (e: DragEvent) => void;
    onDragLeave: (e: DragEvent) => void;
    onDrop: (e: DragEvent) => void;
  };
}

/**
 * Enhanced hook for handling file drag and drop operations
 * Supports both file uploads and file reorganization within the explorer
 */
export function useFileDragDrop({
  onFilesDropped,
  onFileMove,
  onFolderMove,
  accept,
  disabled = false,
  allowFileUpload = true,
  allowFileReorganization = true,
}: UseFileDragDropOptions): UseFileDragDropReturn {
  const [isDragging, setIsDragging] = useState(false);
  const [dragOverTarget, setDragOverTarget] = useState<string>();
  const [draggedFile, setDraggedFile] = useState<FileType | null>(null);

  const validateFile = useCallback((file: File): boolean => {
    if (!accept || accept.length === 0) return true;
    return accept.includes(file.type);
  }, [accept]);

  const isExternalDrag = useCallback((e: DragEvent): boolean => {
    return e.dataTransfer.types.includes('Files');
  }, []);

  const isInternalDrag = useCallback((e: DragEvent): boolean => {
    return e.dataTransfer.types.includes('application/x-file-path');
  }, []);

  // Main container drag handlers (for file uploads)
  const handleDragEnter = useCallback((e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (disabled || !allowFileUpload) return;
    
    if (isExternalDrag(e)) {
      setIsDragging(true);
    }
  }, [disabled, allowFileUpload, isExternalDrag]);

  const handleDragOver = useCallback((e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (disabled) return;
    
    if (isExternalDrag(e) && allowFileUpload) {
      setIsDragging(true);
    }
  }, [disabled, allowFileUpload, isExternalDrag]);

  const handleDragLeave = useCallback((e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (disabled) return;
    
    // Only set isDragging to false if we're leaving the container itself
    if (e.currentTarget === e.target) {
      setIsDragging(false);
      setDragOverTarget(undefined);
    }
  }, [disabled]);

  const handleDrop = useCallback((e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    setDragOverTarget(undefined);
    
    if (disabled) return;

    if (isExternalDrag(e) && allowFileUpload && onFilesDropped) {
      const files = Array.from(e.dataTransfer.files).filter(validateFile);
      if (files.length > 0) {
        onFilesDropped(files);
      }
    }
  }, [disabled, allowFileUpload, onFilesDropped, validateFile, isExternalDrag]);

  // File drag handlers (for internal file reorganization)
  const handleFileDragStart = useCallback((e: DragEvent, file: FileType) => {
    if (disabled || !allowFileReorganization) {
      e.preventDefault();
      return;
    }
    
    setDraggedFile(file);
    e.dataTransfer.setData('application/x-file-path', file.filePath);
    e.dataTransfer.effectAllowed = 'move';
  }, [disabled, allowFileReorganization]);

  const handleFileDragEnd = useCallback((e: DragEvent) => {
    e.preventDefault();
    setDraggedFile(null);
    setDragOverTarget(undefined);
  }, []);

  // Folder drop handlers (for receiving dropped files)
  const createFolderDropHandlers = useCallback((folderPath: string) => {
    return {
      onDragEnter: (e: DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (disabled || !allowFileReorganization) return;
        
        if (isInternalDrag(e)) {
          setDragOverTarget(folderPath);
        }
      },

      onDragOver: (e: DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (disabled || !allowFileReorganization) return;
        
        if (isInternalDrag(e)) {
          e.dataTransfer.dropEffect = 'move';
          setDragOverTarget(folderPath);
        }
      },

      onDragLeave: (e: DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (disabled) return;
        
        // Only clear target if we're actually leaving this folder
        if (e.currentTarget === e.target) {
          setDragOverTarget(undefined);
        }
      },

      onDrop: (e: DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setDragOverTarget(undefined);
        
        if (disabled || !allowFileReorganization) return;

        if (isInternalDrag(e)) {
          const sourceFilePath = e.dataTransfer.getData('application/x-file-path');
          
          if (sourceFilePath && draggedFile) {
            // Determine if we're moving a file or a folder
            const isFolder = !draggedFile.filePath.includes('.');
            
            if (isFolder && onFolderMove) {
              onFolderMove(sourceFilePath, folderPath);
            } else if (!isFolder && onFileMove) {
              // Calculate new file path
              const fileName = sourceFilePath.split('/').pop() || '';
              const newFilePath = folderPath ? `${folderPath}/${fileName}` : fileName;
              
              // Don't move if it's the same location
              if (newFilePath !== sourceFilePath) {
                onFileMove(draggedFile, newFilePath);
              }
            }
          }
        }
      },
    };
  }, [disabled, allowFileReorganization, isInternalDrag, draggedFile, onFileMove, onFolderMove]);

  return {
    isDragging,
    dragOverTarget,
    dragHandlers: {
      onDragEnter: handleDragEnter,
      onDragOver: handleDragOver,
      onDragLeave: handleDragLeave,
      onDrop: handleDrop,
    },
    fileDragHandlers: {
      onDragStart: handleFileDragStart,
      onDragEnd: handleFileDragEnd,
    },
    folderDropHandlers: createFolderDropHandlers,
  };
}

/**
 * Utility function to read files from FileList including directories
 */
export async function readFilesFromDataTransfer(dataTransfer: DataTransfer): Promise<File[]> {
  const files: File[] = [];
  const items = Array.from(dataTransfer.items);

  for (const item of items) {
    if (item.kind === 'file') {
      const entry = item.webkitGetAsEntry?.();
      if (entry) {
        await processEntry(entry, files);
      } else {
        const file = item.getAsFile();
        if (file) files.push(file);
      }
    }
  }

  return files;
}

/**
 * Recursively process directory entries
 */
async function processEntry(entry: FileSystemEntry, files: File[], path = ''): Promise<void> {
  if (entry.isFile) {
    const fileEntry = entry as FileSystemFileEntry;
    const file = await new Promise<File>((resolve, reject) => {
      fileEntry.file(resolve, reject);
    });
    
    // Create a new file with the full path
    const fullPath = path ? `${path}/${file.name}` : file.name;
    const fileWithPath = new File([file], fullPath, { type: file.type });
    files.push(fileWithPath);
  } else if (entry.isDirectory) {
    const dirEntry = entry as FileSystemDirectoryEntry;
    const reader = dirEntry.createReader();
    
    const entries = await new Promise<FileSystemEntry[]>((resolve, reject) => {
      reader.readEntries(resolve, reject);
    });
    
    for (const childEntry of entries) {
      const childPath = path ? `${path}/${entry.name}` : entry.name;
      await processEntry(childEntry, files, childPath);
    }
  }
}
